import Portal from "@mui/material/Portal";
import Snackbar from "@mui/material/Snackbar";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "components/Icons";

import styles from "./styles.module.scss";

type MessageT = string | React.ReactElement;

export enum SnackbarTypes {
  success = "success",
  error = "error",
  loading = "loading",
}

type Props = {
  vertical?: any;
  horizontal?: any;
  openSnackBar?: boolean;
  closeSnackBar?: () => void;
  type?: SnackbarTypes;
  message: MessageT;
  closeIcon?: boolean;
  displayInline?: boolean;
  classes?: any;
  snackBarKey?: string;
  dataTestId?: string;
  autoHideDuration?: number | null;
  dimmed?: boolean;
};

const Message: React.FC<{ message: MessageT }> = props => {
  const { message } = props;
  const { t } = useTranslation();
  if (typeof message === "string") {
    return <span className={styles.messageText}>{t(message as string)}</span>;
  }

  return <div className={styles.messageText}>{message}</div>;
};

export const SnackBar: React.FC<Props> = props => {
  const {
    openSnackBar,
    vertical,
    horizontal,
    closeSnackBar,
    type,
    closeIcon,
    snackBarKey,
    autoHideDuration = null,
    displayInline,
    classes,
    dimmed = false,
    message,
    dataTestId,
  } = props;

  const component = (
    <Snackbar
      data-testid={dataTestId || `snackbar-${type}`}
      anchorOrigin={{ vertical, horizontal }}
      key={snackBarKey}
      open={openSnackBar}
      autoHideDuration={autoHideDuration}
      onClose={closeSnackBar}
      slotProps={{
        content: {
          "aria-describedby": "message-id",
          classes: {
            root: classNames(styles.snackBarContent, classes?.snackbar, {
              [styles.errorSnackBar]: type === "error",
              [styles.successSnackBar]: type === "success",
              [styles.loadingSnackBar]: type === "loading",
            }),
          },
          onClick: (event: React.SyntheticEvent) => {
            event.stopPropagation();
          },
        },
      }}
      classes={{
        root: classNames(styles.root, classes?.root),
        anchorOriginTopCenter: classNames(styles.anchorOriginTopCenter, {
          [styles.dimmed]: dimmed,
        }),
      }}
      onClick={closeSnackBar}
      message={
        <div className={styles.messageWrap}>
          <Message message={message} />
        </div>
      }
      action={
        closeIcon
          ? [
              <CloseIcon
                data-testid="snackbar-close-icon"
                key="close-icon"
                onClick={closeSnackBar}
                className={styles.closeImg}
              />,
            ]
          : undefined
      }
    />
  );

  return displayInline ? component : <Portal>{component}</Portal>;
};
